import type { AnyPresentationOption, OptionsDocument, PresentationOption, PresentationOptionV2 } from 'paintscout'

/** The `schemaVersion` every v2 (`pages → sections → blocks`) document carries. */
export const PRESENTATION_V2_SCHEMA_VERSION = 3

/**
 * Whether a presentation document IS v2-shaped — i.e. has the three-level
 * `pages[] → sections[] → blocks[]` tree rather than v1's two-level `pages[] → sections[]`.
 *
 * This is a type guard, so use it to narrow an AnyPresentationOption you already hold before
 * touching its content tree. It answers a different question from isPresentationNext() below:
 * this one is about the SHAPE of a doc in hand, that one is about WHICH MAP a key belongs to.
 * Prefer this whenever you're about to read `pages`.
 *
 * Note `PresentationOption.version` is NOT the discriminator and never was — getDefaultPresentation
 * stamps `version: 2` on v1-shaped docs for the unrelated legacy "newLook" toggle.
 */
export function isPresentationV2Doc(presentation?: AnyPresentationOption): presentation is PresentationOptionV2 {
  return presentation?.schemaVersion === PRESENTATION_V2_SCHEMA_VERSION
}

/**
 * Whether a presentation key belongs to the v2 "PresentationNext" library.
 *
 * Answers a *selection* question — which of the two parallel `OrderableOption` maps
 * (`options.presentationOptions` vs `options.presentationOptionsV2`) owns this key — which is why
 * it takes the options document and only needs the key. If a key somehow exists in both maps we
 * treat it as v1 so existing quotes keep their current rendering.
 *
 * To narrow a document's content tree, use isPresentationV2Doc() above instead.
 */
export function isPresentationNext(args: {
  options: OptionsDocument
  presentation?: Pick<PresentationOption, 'key'>
}): boolean {
  const { options, presentation } = args
  const key = presentation?.key
  if (!key) {
    return false
  }

  const inV1 = !!options?.options?.presentationOptions?.values?.[key]
  const inV2 = !!options?.options?.presentationOptionsV2?.values?.[key]

  return inV2 && !inV1
}
